import { handleTrack } from '../../../../../../utils/tracker';
import { If } from '../../../../../Atoms/Conditionals';
import { ACCENTURE_SLUG } from '../../../../constants';
import useCopy from '../../../../utils/hooks/use-copy';
import { sanitizeRichText } from '../../../../utils/textUtils';
import IncomeRangeTable from './income-range-table';
import { IPlan, IService } from './types';

const income_range_salary = [
  { range: 'Below 3 lakhs' },
  { range: '3 - 7 lakhs' },
  { range: '7 - 20 lakhs' },
  { range: '20 - 35 lakhs' },
  { range: '35 - 50 lakhs' },
  { range: 'Above 50 lakhs' },
];

const income_range_cg = [
  { range: 'Below 3 lakhs' },
  { range: '3 - 7 lakhs' },
  { range: '7 - 20 lakhs' },
  { range: '20 - 35 lakhs' },
  { range: 'Above 35 lakhs' },
];

const income_range_foreign = [
  { range: 'Below 7 lakhs' },
  { range: '7 - 20 lakhs' },
  { range: 'Above 20 lakhs' },
];

const SelfFilingModal = ({
  plans,
  currentIndex,
  slug,
}: {
  plans: IPlan[];
  currentIndex: number;
  slug?: string;
}) => {
  const plan = plans?.[currentIndex];
  const delay = 1000;
  const { copied, handleCopyCoupon } = useCopy(plan?.coupon?.code || '', delay);
  const isAccenture = slug === ACCENTURE_SLUG;
  return (
    <div>
      <div className="flex bg-blue-50 justify-between items-center py-6 ">
        <div className="flex justify-between items-center gap-3 px-6">
          <img
            src="https://assets.clear.in/cleartax/images/1746711749_salaryincome2.svg"
            alt="Salary Income Plan"
            className="w-16 h-16 sm:w-16 sm:h-16"
          />
          <div className="font-bold text-s-24">
            {plan?.planDetails.name.split('-')[0]}
          </div>
        </div>
        {plan?.coupon && (
          <div
            className="bg-green-100 rounded-md flex justify-between items-center h-12 mr-6 w-80 px-4 sm:hidden cursor-pointer"
            onClick={() => {
              handleCopyCoupon();
              handleTrack({
                label: `plan-card-modal_copy`,
                obj: null,
              });
            }}
          >
            <div className="font-bold text-s-20 sm:font-semibold text-green-700">
              Get {Math.floor(plan.coupon?.discountValue)}% OFF
            </div>
            <div className="flex items-end">
              <div className="font-semibold text-base text-blue-350">
                {plan?.coupon?.code}
              </div>
              <img
                className="w-6 h-6 ml-1.5"
                src={
                  copied
                    ? 'https://assets.clear.in/cleartax/images/1712001391_checkcircle1.svg'
                    : 'https://assets.clear.in/cleartax/images/1714203952_copy.svg'
                }
                alt="copy coupon"
              />
            </div>
          </div>
        )}
      </div>
      <div className=" bg-white">
        <div className="flex bg-white justify-between py-10 px-6 sm:block sm:h-96 sm:overflow-auto sm:p-0">
          <div className=" w-5/12 sm:w-full sm:py-6 sm:px-4 ">
            <div className="text-s-20 font-bold pb-6 sm:text-base sm:pb-3">
              Plans & Prices
            </div>
            <div className="bg-gray-100 font-bold text-s-14 flex justify-between py-3 px-2">
              <div>Taxable Income</div>
              <div>Amount</div>
            </div>
            <If condition={isAccenture}>
              {(() => {
                let startIndex = 0;
                let endIndex = income_range_cg.length;

                if (currentIndex === 0) {
                  startIndex = 0;
                  endIndex = 5;
                } else if (currentIndex === 5) {
                  startIndex = 5;
                  endIndex = 10;
                } else if (currentIndex === 10) {
                  startIndex = 10;
                  endIndex = 13;
                }

                const displayedIncomeRange = income_range_cg.slice(
                  startIndex,
                  endIndex
                );

                return (
                  <IncomeRangeTable
                    incomeRange={
                      currentIndex !== 10
                        ? income_range_cg
                        : income_range_foreign
                    }
                    plans={plans}
                    startIndex={startIndex}
                    displayedIncomeRange={displayedIncomeRange}
                  />
                );
              })()}
            </If>
            <If condition={!isAccenture}>
              {(() => {
                let planStartIndex = 0;
                let selectedIncomeRange;

                if (currentIndex >= 0 && currentIndex <= 5) {
                  // First 6 plans (0-5) use salary income range
                  selectedIncomeRange = income_range_salary;
                  planStartIndex = 0;
                } else if (currentIndex >= 6 && currentIndex <= 10) {
                  // Next 5 plans (6-10) use capital gains income range
                  selectedIncomeRange = income_range_cg;
                  planStartIndex = 6;
                } else if (currentIndex >= 11 && currentIndex <= 13) {
                  // Last 3 plans (11-13) use foreign income range
                  selectedIncomeRange = income_range_foreign;
                  planStartIndex = 11;
                } else {
                  // Default fallback
                  selectedIncomeRange = income_range_salary;
                  planStartIndex = 0;
                }

                // displayedIncomeRange should be the full selectedIncomeRange
                const displayedIncomeRange = selectedIncomeRange;

                return (
                  <IncomeRangeTable
                    incomeRange={selectedIncomeRange}
                    plans={plans}
                    startIndex={planStartIndex}
                    displayedIncomeRange={displayedIncomeRange}
                  />
                );
              })()}
            </If>
          </div>
          <div className="flex justify-center">
            <div className="bg-orange-100 border border-orange-200 hidden justify-between items-center h-12 mr-6 w-80 px-4 sm:flex sm:my-6 sm:mx-4">
              <div className="font-bold text-s-20 sm:font-semibold">
                Extra {plan?.coupon?.discountValue}% OFF
              </div>
              <div className="flex items-end">
                <div className="font-semibold text-base text-orange-300">
                  {plan?.coupon?.code}
                </div>
                <img
                  src="https://assets.clear.in/cleartax/images/1746722910_copy.svg"
                  className="sm:h-6 sm:w-6"
                />
              </div>
            </div>
          </div>

          <img
            src="https://assets.clear.in/cleartax/images/1747053270_vector7809.svg"
            className="sm:hidden"
          />
          <div className="w-[44%] sm:w-full sm:bg-blue-50">
            <div className="text-s-20 font-bold sm:pt-6 sm:px-4">
              Income & Services Covered
            </div>
            <div className="px-6 py-8 sm:pt-4 sm:px-4">
              <ul className="list-inside space-y-1 text-sm text-grey-700">
                {plan?.planDetails?.servicesIncluded?.map(
                  (feat: IService, idx) => (
                    <li key={idx} className="flex align-top">
                      <img
                        src="https://assets.clear.in/cleartax/images/1746722818_checkcircle4.svg"
                        className="w-6 h-6"
                      />
                      <div
                        className="text-font-500 text-s-16 font-medium sm:text-s-14"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeRichText(feat.richText),
                        }}
                      ></div>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
        <hr />
        <div className="flex justify-between py-6 px-6">
          <div className="w-5/12 text-base font-medium sm:hidden">
            Start filing for <strong>FREE</strong> & apply the{' '}
            <strong>COUPON</strong> during checkout.
          </div>
          <a
            className="w-5/12 text-white bg-blue-350 items-center justify-center flex rounded-sm sm:w-full sm:py-3 font-bold text-base"
            href="/filing?&utm_ref=itr_partner_page"
            onClick={(e) =>
              handleTrack({
                label: 'plan-card-modal_',
                obj: e.target,
              })
            }
          >
            Start Filing
          </a>
        </div>
      </div>
    </div>
  );
};

export default SelfFilingModal;
